import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Download, ImageUp, Save, Search, Send, Trash2, UserPlus, UtensilsCrossed } from 'lucide-react';
import { almuerzosAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const TIPOS_MINUTA = ['comun', 'alternativa', 'hipocalorico', 'ejecutivo'];
const NOMBRE_TIPO = {
  comun: 'Común', alternativa: 'Alternativa', hipocalorico: 'Hipocalórico', ejecutivo: 'Ejecutivo',
  hipoc_vegetariano: 'Hipocalórico vegetariano', dieta_liviana: 'Dieta liviana',
  premium_verde: 'Premium · Ensalada verde', premium_cesar: 'Premium · Ensalada César', premium_pollo_palmito: 'Premium · Pollo palmito',
};
const COLOR_TIPO = {
  comun: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  alternativa: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  hipocalorico: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  ejecutivo: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  hipoc_vegetariano: 'bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-200',
  dieta_liviana: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
  premium_verde: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  premium_cesar: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  premium_pollo_palmito: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
};
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const aFecha = (texto) => new Date(`${texto}T12:00:00`);
const sumarDias = (texto, n) => {
  const d = aFecha(texto);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const rangoSemana = (lunes) => {
  const a = aFecha(lunes);
  const b = aFecha(sumarDias(lunes, 4));
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()} al ${b.getDate()} de ${MESES[b.getMonth()]} de ${b.getFullYear()}`
    : `${a.getDate()} de ${MESES[a.getMonth()]} al ${b.getDate()} de ${MESES[b.getMonth()]} de ${b.getFullYear()}`;
};
const cierreTexto = (c) => (c ? new Date(c).toLocaleString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : null);

const campo = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500';
const boton = 'inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50';

function lineas(detalle) {
  return String(detalle || '').split('\n').map((x) => x.trim()).filter(Boolean);
}

// ---------------------------------------------------------------- Mi pedido
function MiPedido({ datos, recargar }) {
  const { showToast } = useToast();
  const { semana } = datos;
  const [sitio, setSitio] = useState(datos.miSitio || '');
  const [borrador, setBorrador] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const inicial = {};
    (datos.misPedidos || []).forEach((p) => { inicial[p.dia] = p.tipo || null; });
    setBorrador(inicial);
    setSitio(datos.miSitio || '');
  }, [datos]);

  const cambiado = useMemo(() => {
    const original = {};
    (datos.misPedidos || []).forEach((p) => { original[p.dia] = p.tipo || null; });
    return sitio !== (datos.miSitio || '') || [1, 2, 3, 4, 5].some((d) => (borrador[d] || null) !== (original[d] || null));
  }, [borrador, sitio, datos]);

  if (!semana) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-10 text-center">
        <UtensilsCrossed className="w-10 h-10 mx-auto text-gray-300" />
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Todavía no se publica la minuta de esta semana.</p>
      </div>
    );
  }

  const guardar = async () => {
    if (!sitio) { showToast('Falta tu ubicación', 'Elige si almuerzas en Huingan o en El Sauce.', 'error'); return; }
    setGuardando(true);
    try {
      await almuerzosAPI.guardarMisPedidos(semana.id, { sitio, pedidos: [1, 2, 3, 4, 5].map((dia) => ({ dia, tipo: borrador[dia] || null })) });
      showToast('Listo', 'Tu pedido de la semana quedó guardado.', 'success');
      await recargar();
    } catch (e) {
      showToast('Error', e.response?.data?.message || 'No se pudo guardar el pedido', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const sinElegir = [1, 2, 3, 4, 5].filter((d) => borrador[d] === undefined).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div>
          <p className="text-xs text-gray-500">{semana.titulo || 'Minuta de la semana'}</p>
          {semana.cerrada ? (
            <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">Pedidos cerrados</span>
          ) : (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {semana.cierraEl ? <>Puedes pedir o cambiar hasta el <b>{cierreTexto(semana.cierraEl)}</b></> : 'Pedidos abiertos'}
            </p>
          )}
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">¿Dónde almuerzas?</p>
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {datos.sitios.map((s, i) => (
              <button
                key={s}
                type="button"
                disabled={semana.cerrada}
                onClick={() => setSitio(s)}
                className={`px-4 py-2 text-xs font-bold ${i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${sitio === s ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {DIAS.map((nombre, i) => {
          const dia = i + 1;
          const opciones = semana.opciones.filter((o) => o.dia === dia);
          const elegido = borrador[dia];
          return (
            <section key={dia} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">{nombre}</h3>
              <div className="space-y-2">
                {opciones.map((o) => {
                  const marcado = elegido === o.tipo;
                  return (
                    <button
                      key={o.tipo}
                      type="button"
                      disabled={semana.cerrada}
                      onClick={() => setBorrador((b) => ({ ...b, [dia]: marcado ? null : o.tipo }))}
                      aria-pressed={marcado}
                      className={`w-full text-left rounded-lg border p-3 transition-colors ${marcado ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'} disabled:opacity-70`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${COLOR_TIPO[o.tipo]}`}>{NOMBRE_TIPO[o.tipo]}</span>
                        {marcado && <Check className="w-4 h-4 text-blue-600" />}
                      </span>
                      <ul className="mt-2 space-y-0.5 text-xs text-gray-700 dark:text-gray-300">
                        {lineas(o.detalle).map((l, k) => <li key={k}>{l}</li>)}
                      </ul>
                    </button>
                  );
                })}
                {datos.fijos?.length > 0 && (
                  <div className="pt-1">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">Otras opciones (siempre disponibles)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {datos.fijos.map((f) => {
                        const marcado = elegido === f.tipo;
                        return (
                          <button
                            key={f.tipo}
                            type="button"
                            disabled={semana.cerrada}
                            aria-pressed={marcado}
                            onClick={() => setBorrador((b) => ({ ...b, [dia]: marcado ? null : f.tipo }))}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${marcado ? 'border-blue-600 ring-1 ring-blue-600 ' + COLOR_TIPO[f.tipo] : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
                          >
                            {marcado && <Check className="inline w-3 h-3 mr-1" />}{f.nombre}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  disabled={semana.cerrada}
                  onClick={() => setBorrador((b) => ({ ...b, [dia]: null }))}
                  aria-pressed={elegido === null}
                  className={`w-full text-left rounded-lg border px-3 py-2 text-xs transition-colors ${elegido === null ? 'border-gray-400 bg-gray-100 dark:bg-gray-700 font-semibold' : 'border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
                >
                  No almuerzo este día
                </button>
              </div>
            </section>
          );
        })}
      </div>

      {!semana.cerrada && (
        <div className="sticky bottom-3 flex flex-wrap items-center justify-between gap-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg px-4 py-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {sinElegir > 0 ? `Te faltan ${sinElegir} día(s) por elegir (si no almuerzas, marca «No almuerzo»).` : cambiado ? 'Tienes cambios sin guardar.' : 'Tu pedido está al día.'}
          </p>
          <button type="button" onClick={guardar} disabled={guardando || !cambiado} className={`${boton} bg-blue-600 hover:bg-blue-700 text-white`}>
            <Save className="w-4 h-4" />{guardando ? 'Guardando…' : 'Guardar pedido'}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Anotar el pedido de otra persona (coordinadores)
function AnotarPedido({ semana, datos, alCerrar, alGuardar }) {
  const { showToast } = useToast();
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [persona, setPersona] = useState(null); // { id, nombre, invitado } o { invitadoNuevo: true }
  const [invitado, setInvitado] = useState({ nombre: '', empresa: '' });
  const [sitio, setSitio] = useState('');
  const [borrador, setBorrador] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (q.trim().length < 3) { setResultados([]); return undefined; }
    const t = setTimeout(() => { almuerzosAPI.buscarPersonas(q.trim()).then(({ data }) => setResultados(data)).catch(() => setResultados([])); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const elegir = async (p) => {
    setPersona(p);
    setQ('');
    setResultados([]);
    try {
      const { data } = await almuerzosAPI.pedidosDe(semana.id, p.id);
      const b = {};
      data.pedidos.forEach((x) => { b[x.dia] = x.tipo || '__no'; });
      setBorrador(b);
      setSitio(data.sitio || '');
    } catch { setBorrador({}); }
  };

  const guardar = async () => {
    if (!sitio) { showToast('Falta la ubicación', 'Indica si almuerza en Huingan o en El Sauce.', 'error'); return; }
    const pedidos = Object.entries(borrador).filter(([, v]) => v !== '' && v !== undefined).map(([dia, v]) => ({ dia: Number(dia), tipo: v === '__no' ? null : v }));
    if (!pedidos.length) { showToast('Sin cambios', 'Elige al menos un día.', 'error'); return; }
    setGuardando(true);
    try {
      await almuerzosAPI.guardarPedidosDe(semana.id, persona.invitadoNuevo
        ? { invitado, sitio, pedidos }
        : { userId: persona.id, sitio, pedidos });
      showToast('Listo', 'Pedido guardado.', 'success');
      alGuardar();
    } catch (e) {
      showToast('Error', e.response?.data?.message || 'No se pudo guardar', 'error');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[9000] bg-black/50 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget && !guardando) alCerrar(); }}>
      <div className="min-h-full flex items-start justify-center p-4">
        <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-4 my-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Anotar el pedido de otra persona</h3>
          <p className="text-xs text-gray-500">Sirve también después del cierre. Úsalo para quien no entra al portal o para una visita.</p>

          {!persona ? (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o correo (mínimo 3 letras)…" className={`${campo} pl-9`} />
              </div>
              {resultados.length > 0 && (
                <ul className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 max-h-60 overflow-y-auto">
                  {resultados.map((r) => (
                    <li key={r.id}>
                      <button type="button" onClick={() => elegir(r)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40">
                        {r.nombre} <span className="text-xs text-gray-500">{r.empresa || ''}{r.invitado ? ' · invitado' : ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" onClick={() => { setPersona({ invitadoNuevo: true }); setBorrador({}); }} className="text-sm font-semibold text-blue-600 hover:underline">
                La persona no está: anotarla como invitado
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                {persona.invitadoNuevo ? (
                  <div className="grid grid-cols-2 gap-2 flex-1">
                    <input value={invitado.nombre} onChange={(e) => setInvitado((x) => ({ ...x, nombre: e.target.value }))} placeholder="Nombre del invitado" className={campo} />
                    <input value={invitado.empresa} onChange={(e) => setInvitado((x) => ({ ...x, empresa: e.target.value }))} placeholder="Empresa (opcional)" className={campo} />
                  </div>
                ) : (
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{persona.nombre}{persona.invitado ? ' (invitado)' : ''}</p>
                )}
                <button type="button" onClick={() => setPersona(null)} className="text-xs text-gray-500 hover:underline shrink-0">Cambiar</button>
              </div>

              <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {datos.sitios.map((s, i) => (
                  <button key={s} type="button" onClick={() => setSitio(s)} className={`px-4 py-2 text-xs font-bold ${i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${sitio === s ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300'}`}>{s}</button>
                ))}
              </div>

              <div className="space-y-2">
                {DIAS.map((nombre, i) => {
                  const dia = i + 1;
                  const delDia = semana.opciones.filter((o) => o.dia === dia).map((o) => o.tipo);
                  return (
                    <label key={dia} className="flex items-center gap-3">
                      <span className="w-24 text-sm font-medium text-gray-700 dark:text-gray-300">{nombre}</span>
                      <select value={borrador[dia] ?? ''} onChange={(e) => setBorrador((b) => ({ ...b, [dia]: e.target.value }))} className={campo}>
                        <option value="">— sin cambios —</option>
                        <option value="__no">No almuerza</option>
                        {delDia.map((t) => <option key={t} value={t}>{NOMBRE_TIPO[t]}</option>)}
                        {datos.fijos.map((f) => <option key={f.tipo} value={f.tipo}>{f.nombre}</option>)}
                      </select>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" disabled={guardando} onClick={alCerrar} className={`${boton} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200`}>Cerrar</button>
            {persona && <button type="button" disabled={guardando} onClick={guardar} className={`${boton} bg-blue-600 hover:bg-blue-700 text-white`}><Save className="w-4 h-4" />{guardando ? 'Guardando…' : 'Guardar pedido'}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Envío a Seralco
function EnviarSeralco({ semana, alCerrar, alEnviar }) {
  const { showToast } = useToast();
  const [para, setPara] = useState('');
  const [cc, setCc] = useState('');
  const [cargado, setCargado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    almuerzosAPI.correoSeralco().then(({ data }) => { setPara(data.para || ''); setCc(data.cc || ''); setCargado(true); }).catch(() => setCargado(true));
  }, []);

  const enviar = async () => {
    setEnviando(true);
    try {
      await almuerzosAPI.guardarCorreoSeralco({ para, cc });
      await almuerzosAPI.enviarSeralco(semana.id);
      showToast('Enviado', 'El cuadro de almuerzos se envió a Seralco.', 'success');
      alEnviar(new Date().toISOString());
    } catch (e) {
      showToast('Error', e.response?.data?.message || 'No se pudo enviar el correo', 'error');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[9000] bg-black/50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !enviando) alCerrar(); }}>
      <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Enviar el cuadro a Seralco</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">Se enviará por correo el Excel «Cuadro general de almuerzo» de la semana del {rangoSemana(semana.lunes)} (Huingan y El Sauce). Revisa los destinatarios: el envío es real.</p>
        <label className="block"><span className="block text-xs font-semibold text-gray-500 mb-1">Para</span>
          <input value={para} onChange={(e) => setPara(e.target.value)} disabled={!cargado} className={campo} placeholder="correo@seralco.cl" /></label>
        <label className="block"><span className="block text-xs font-semibold text-gray-500 mb-1">Con copia (opcional)</span>
          <input value={cc} onChange={(e) => setCc(e.target.value)} disabled={!cargado} className={campo} placeholder="otro@correo.cl" /></label>
        <p className="text-[11px] text-gray-400">Varios correos se separan con coma. Quedan guardados para las próximas semanas.</p>
        <div className="flex justify-end gap-2">
          <button type="button" disabled={enviando} onClick={alCerrar} className={`${boton} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200`}>Cancelar</button>
          <button type="button" disabled={enviando || !para.trim()} onClick={enviar} className={`${boton} bg-blue-600 hover:bg-blue-700 text-white`}><Send className="w-4 h-4" />{enviando ? 'Enviando…' : 'Enviar ahora'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Resumen (coordinadores)
function Resumen({ semana, datos }) {
  const { showToast } = useToast();
  const [r, setR] = useState(null);
  const [sitio, setSitio] = useState('todos');
  const [q, setQ] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [anotando, setAnotando] = useState(false);
  const [version, setVersion] = useState(0);
  const [yaEnviado, setYaEnviado] = useState(null);

  useEffect(() => {
    if (!semana) return;
    almuerzosAPI.resumen(semana.id).then(({ data }) => { setR(data); setYaEnviado(data.semana.enviadoSeralcoEl || null); }).catch((e) => showToast('Error', e.response?.data?.message || 'No se pudo cargar el resumen', 'error'));
  }, [semana, showToast, version]);

  if (!semana) return <p className="text-sm text-gray-500">No hay minuta cargada para esta semana.</p>;
  if (!r) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  const personas = r.personas.filter((p) => (sitio === 'todos' || p.sitio === sitio) && (!q || `${p.nombre} ${p.empresa}`.toLowerCase().includes(q.toLowerCase())));
  const sitios = Object.keys(r.conteos);
  const tipos = (r.tipos || []).map((t) => t.tipo);

  const descargar = async (formato) => {
    try {
      const resp = await almuerzosAPI.excel(semana.id, formato);
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = formato === 'cuadro' ? `CUADRO_GENERAL_ALMUERZO_${semana.lunes}.xlsx` : `LISTA_DE_ALMUERZO_${semana.lunes}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Error', 'No se pudo generar el Excel', 'error');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          <b>{r.personas.length}</b> persona(s) han pedido esta semana.
          {yaEnviado && <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">Enviado a Seralco el {new Date(yaEnviado).toLocaleString('es-CL')}</span>}
        </p>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => descargar('lista')} className={`${boton} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700`}>
            <Download className="w-4 h-4" />Lista por persona
          </button>
          <button type="button" onClick={() => descargar('cuadro')} className={`${boton} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700`}>
            <Download className="w-4 h-4" />Cuadro para Seralco
          </button>
          <button type="button" onClick={() => setAnotando(true)} className={`${boton} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700`}>
            <UserPlus className="w-4 h-4" />Anotar a otra persona
          </button>
          <button type="button" onClick={() => setEnviando(true)} className={`${boton} bg-blue-600 hover:bg-blue-700 text-white`}>
            <Send className="w-4 h-4" />Enviar a Seralco
          </button>
        </div>
      </div>

      {sitios.length === 0 ? (
        <p className="text-sm text-gray-500">Todavía nadie ha elegido.</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {sitios.map((s) => (
            <section key={s} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
              <h3 className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700">Platos a preparar · {s}</h3>
              <table className="w-full text-xs">
                <thead className="text-gray-500 bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="text-left px-3 py-2">Día</th>
                    {tipos.map((t) => <th key={t} className="text-center px-3 py-2">{NOMBRE_TIPO[t]}</th>)}
                    <th className="text-center px-3 py-2">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {DIAS.map((d, i) => {
                    const por = r.conteos[s]?.[i + 1] || {};
                    const total = Object.values(por).reduce((a, b) => a + b, 0);
                    return (
                      <tr key={d} className="text-gray-700 dark:text-gray-300">
                        <td className="px-3 py-2 font-medium">{d}</td>
                        {tipos.map((t) => <td key={t} className="px-3 py-2 text-center tabular-nums">{por[t] || <span className="text-gray-300">0</span>}</td>)}
                        <td className="px-3 py-2 text-center font-bold tabular-nums">{total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      {anotando && <AnotarPedido semana={r.semana ? { ...semana, opciones: r.opciones } : semana} datos={datos} alCerrar={() => setAnotando(false)} alGuardar={() => { setAnotando(false); setVersion((v) => v + 1); }} />}
      {enviando && <EnviarSeralco semana={semana} alCerrar={() => setEnviando(false)} alEnviar={(cuando) => { setYaEnviado(cuando); setEnviando(false); }} />}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar persona o empresa…" className={`${campo} pl-9`} />
        </div>
        {['todos', ...sitios].map((s) => (
          <button key={s} type="button" onClick={() => setSitio(s)} className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${sitio === s ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'}`}>
            {s === 'todos' ? 'Todas' : s}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Persona</th>
              <th className="text-left px-4 py-3">Ubicación</th>
              {DIAS.map((d) => <th key={d} className="text-left px-3 py-3">{d.slice(0, 3)}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {personas.map((p) => (
              <tr key={p.userId} className="text-gray-700 dark:text-gray-300">
                <td className="px-4 py-2.5"><p className="font-medium text-gray-900 dark:text-white">{p.nombre}{p.invitado && <span className="ml-1.5 text-[10px] uppercase text-gray-400">invitado</span>}</p><p className="text-xs text-gray-500">{p.empresa}</p></td>
                <td className="px-4 py-2.5 text-xs">{p.sitio}</td>
                {DIAS.map((d, i) => {
                  const t = p.dias[i + 1];
                  return <td key={d} className="px-3 py-2.5">{t ? <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${COLOR_TIPO[t]}`}>{NOMBRE_TIPO[t]}</span> : <span className="text-gray-300">—</span>}</td>;
                })}
              </tr>
            ))}
            {personas.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">Sin resultados.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Minuta (coordinadores)
function Minuta({ datos, recargar, esAdmin }) {
  const { showToast } = useToast();
  const { semana, lunes } = datos;
  const [titulo, setTitulo] = useState('');
  const [cierre, setCierre] = useState('');
  const [publicada, setPublicada] = useState(true);
  const [texto, setTexto] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [coordinadores, setCoordinadores] = useState([]);
  const [correo, setCorreo] = useState('');
  const [leyendo, setLeyendo] = useState(false);
  const [errorLectura, setErrorLectura] = useState('');

  useEffect(() => {
    setTitulo(semana?.titulo || '');
    setCierre(semana?.cierraEl || `${sumarDias(lunes, -3)}T16:00`); // por defecto, el viernes anterior a las 16:00
    setPublicada(semana ? semana.publicada : true);
    const t = {};
    (semana?.opciones || []).forEach((o) => { t[`${o.dia}:${o.tipo}`] = o.detalle; });
    setTexto(t);
  }, [semana, lunes]);

  const cargarCoordinadores = useCallback(() => {
    if (esAdmin) almuerzosAPI.coordinadores().then(({ data }) => setCoordinadores(data)).catch(() => setCoordinadores([]));
  }, [esAdmin]);
  useEffect(() => { cargarCoordinadores(); }, [cargarCoordinadores]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const opciones = Object.entries(texto).map(([k, detalle]) => { const [dia, tipo] = k.split(':'); return { dia: Number(dia), tipo, detalle }; });
      await almuerzosAPI.guardarSemana({ lunes, titulo, cierraEl: cierre || null, publicada, opciones });
      showToast('Listo', publicada ? 'La minuta quedó publicada.' : 'La minuta quedó guardada (sin publicar).', 'success');
      await recargar();
    } catch (e) {
      showToast('Error', e.response?.data?.message || 'No se pudo guardar la minuta', 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Lee las fotos de la minuta de Seralco y rellena los platos; no guarda nada hasta que el coordinador revise y presione Guardar
  const importarImagenes = async (e) => {
    const archivos = [...(e.target.files || [])];
    e.target.value = '';
    if (!archivos.length) return;
    const fd = new FormData();
    archivos.forEach((a) => fd.append('imagenes', a));
    setLeyendo(true);
    setErrorLectura('');
    try {
      const { data } = await almuerzosAPI.importarMinuta(fd);
      const t = {};
      data.opciones.forEach((o) => { t[`${o.dia}:${o.tipo}`] = o.detalle; });
      setTexto(t);
      if (data.titulo && !titulo) setTitulo(data.titulo);
      const vacios = ['comun', 'alternativa', 'hipocalorico', 'ejecutivo'].filter((tp) => !data.opciones.some((o) => o.tipo === tp));
      if (vacios.length) {
        const msg = `La lectura no encontró platos para: ${vacios.join(', ')}. Complétalos a mano o vuelve a cargar las imágenes.`;
        setErrorLectura(msg);
        showToast('Lectura incompleta', msg, 'warning');
      } else {
        showToast('Minuta leída', `Se cargaron ${data.opciones.length} opciones. Revísalas antes de guardar.`, 'success');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'No se pudo leer la minuta. Escríbela a mano.';
      setErrorLectura(msg);
      showToast('No se pudo leer', msg, 'error');
    } finally {
      setLeyendo(false);
    }
  };

  const copiarSemanaAnterior = async () => {
    try {
      const { data } = await almuerzosAPI.semana(sumarDias(lunes, -7));
      if (!data.semana) { showToast('Sin datos', 'La semana anterior no tiene minuta.', 'error'); return; }
      const t = {};
      data.semana.opciones.forEach((o) => { t[`${o.dia}:${o.tipo}`] = o.detalle; });
      setTexto(t);
      showToast('Copiada', 'Se copiaron los platos de la semana anterior: edítalos y guarda.', 'success');
    } catch {
      showToast('Error', 'No se pudo leer la semana anterior', 'error');
    }
  };

  const ajustarCoordinador = async (email, activo) => {
    try {
      await almuerzosAPI.ajustarCoordinador({ email, activo });
      setCorreo('');
      cargarCoordinadores();
    } catch (e) {
      showToast('Error', e.response?.data?.message || 'No se pudo cambiar', 'error');
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <label className="block"><span className="block text-xs font-semibold text-gray-500 mb-1">Título (ej. «Semana 46»)</span>
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} className={campo} placeholder="Menú almuerzo S 46" /></label>
        <label className="block"><span className="block text-xs font-semibold text-gray-500 mb-1">Los pedidos cierran el</span>
          <input type="datetime-local" value={cierre} onChange={(e) => setCierre(e.target.value)} className={campo} /></label>
        <label className="flex items-end gap-2 pb-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input type="checkbox" checked={publicada} onChange={(e) => setPublicada(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600" />
          Publicada (visible para todos)
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        {datos.lecturaImagenes === false ? (
          <button type="button" onClick={() => setErrorLectura('La lectura automática de las imágenes todavía no está activada en el servidor (faltan AZURE_OPENAI_ENDPOINT, AZURE_OPENAI_KEY y AZURE_OPENAI_DEPLOYMENT en el .env de HelpDesk). Mientras tanto, escribe la minuta a mano o copia la de la semana anterior.')} className={`${boton} bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900`}>
            <ImageUp className="w-4 h-4" />Cargar desde las imágenes
          </button>
        ) : (
          <label className={`${boton} bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 cursor-pointer ${leyendo ? 'opacity-60 pointer-events-none' : ''}`}>
            <ImageUp className="w-4 h-4" />{leyendo ? 'Leyendo la minuta…' : 'Cargar desde las imágenes'}
            <input type="file" accept="image/*" multiple className="hidden" onChange={importarImagenes} />
          </label>
        )}
        <button type="button" onClick={copiarSemanaAnterior} className={`${boton} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700`}>Copiar la semana anterior</button>
        <button type="button" onClick={() => setTexto({})} className={`${boton} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700`}><Trash2 className="w-4 h-4" />Vaciar</button>
      </div>
      {errorLectura && <div role="alert" className="text-sm rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 px-3 py-2">{errorLectura}</div>}
      <p className="text-xs text-gray-500">Escribe cada plato en una línea. Deja vacía una opción que ese día no exista (por ejemplo, la alternativa de algunos días).</p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {DIAS.map((nombre, i) => (
          <section key={nombre} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">{nombre}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {TIPOS_MINUTA.map((t) => (
                <label key={t} className="block">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold mb-1 ${COLOR_TIPO[t]}`}>{NOMBRE_TIPO[t]}</span>
                  <textarea
                    rows={4}
                    value={texto[`${i + 1}:${t}`] || ''}
                    onChange={(e) => setTexto((x) => ({ ...x, [`${i + 1}:${t}`]: e.target.value }))}
                    placeholder={t === 'ejecutivo' ? 'Entrada: …\nPlato de fondo: …\nPostre: …' : 'Entrada\nPlato de fondo\nPostre'}
                    className={`${campo} text-xs`}
                  />
                </label>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={guardar} disabled={guardando} className={`${boton} bg-blue-600 hover:bg-blue-700 text-white`}><Save className="w-4 h-4" />{guardando ? 'Guardando…' : 'Guardar minuta'}</button>
      </div>

      {esAdmin && (
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">Coordinadores de almuerzos</h3>
          <p className="text-xs text-gray-500">Pueden cargar la minuta y ver los pedidos (además de los administradores).</p>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {coordinadores.map((c) => (
              <li key={c.userId} className="flex items-center justify-between py-2 text-sm">
                <span>{c.nombre} <span className="text-xs text-gray-500">{c.email}</span></span>
                <button type="button" onClick={() => ajustarCoordinador(c.email, false)} className="text-xs text-red-600 hover:underline">Quitar</button>
              </li>
            ))}
            {coordinadores.length === 0 && <li className="py-2 text-sm text-gray-500">Aún no hay coordinadores.</li>}
          </ul>
          <div className="flex gap-2">
            <input value={correo} onChange={(e) => setCorreo(e.target.value)} placeholder="correo@empresa.cl" className={campo} />
            <button type="button" disabled={!correo.trim()} onClick={() => ajustarCoordinador(correo, true)} className={`${boton} bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900`}>Agregar</button>
          </div>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- página
export default function Almuerzos() {
  const { showToast } = useToast();
  const [fecha, setFecha] = useState(null); // cualquier día de la semana mostrada
  const [datos, setDatos] = useState(null);
  const [vista, setVista] = useState('pedido');
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async (f) => {
    setCargando(true);
    try {
      const { data } = await almuerzosAPI.semana(f);
      setDatos(data);
    } catch (e) {
      showToast('Error', e.response?.data?.message || 'No se pudo cargar la minuta', 'error');
    } finally {
      setCargando(false);
    }
  }, [showToast]);

  useEffect(() => { cargar(fecha); }, [fecha, cargar]);

  const mover = (dias) => setFecha(sumarDias(datos?.lunes, dias));

  const vistas = [['pedido', 'Mi pedido'], ...(datos?.esCoordinador ? [['resumen', 'Pedidos de la semana'], ['minuta', 'Cargar minuta']] : [])];

  return (
    <div className="space-y-5 pb-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2"><UtensilsCrossed className="w-6 h-6 text-blue-600" />Almuerzos</h1>
          <p className="text-gray-600 dark:text-gray-400">Elige tu almuerzo de la semana (Huingan y El Sauce)</p>
          {datos && <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${datos.esCoordinador ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>{datos.esCoordinador ? 'Coordinador de almuerzos' : 'Participante'}</span>}
        </div>
        {datos && (
          <div className="inline-flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-1">
            <button type="button" onClick={() => mover(-7)} aria-label="Semana anterior" className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-2 text-sm font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">{rangoSemana(datos.lunes)}</span>
            <button type="button" onClick={() => mover(7)} aria-label="Semana siguiente" className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      {vistas.length > 1 && (
        <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
          {vistas.map(([id, texto]) => (
            <button key={id} type="button" aria-pressed={vista === id} onClick={() => setVista(id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium ${vista === id ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}>
              {texto}
            </button>
          ))}
        </div>
      )}

      {cargando && !datos ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
      ) : datos && (
        <>
          {vista === 'pedido' && <MiPedido datos={datos} recargar={() => cargar(datos.lunes)} />}
          {vista === 'resumen' && <Resumen semana={datos.semana} datos={datos} />}
          {vista === 'minuta' && <Minuta datos={datos} recargar={() => cargar(datos.lunes)} esAdmin />}
        </>
      )}
    </div>
  );
}
